import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import styles from './RestrictedYouTubePlayer.module.css';

declare global {
    interface Window {
        YT: any;
        onYouTubeIframeAPIReady: (() => void) | undefined;
    }
}

export interface RestrictedYouTubePlayerProps {
    src: string;
    onEnded?: () => void;
    onTimeUpdate?: (data: { currentTime: number; duration: number; percentWatched: number }) => void;
    onLoadedMetadata?: (data: { duration: number }) => void;
    onPlay?: () => void;
    onPause?: () => void;
    initialTime?: number;
    allowSeeking?: boolean;
    autoPlay?: boolean;
}

const callbacks: (() => void)[] = [];
let scriptLoaded = false;

const loadYouTubeSDK = (callback: () => void) => {
    if (window.YT && window.YT.Player) {
        callback();
        return;
    }

    callbacks.push(callback);

    if (scriptLoaded) return;
    scriptLoaded = true;

    const scriptTags = Array.from(document.getElementsByTagName('script'));
    const isAlreadyAdded = scriptTags.some(s => s.src === 'https://www.youtube.com/iframe_api');

    if (!isAlreadyAdded) {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
    }

    const previousOnReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
        if (previousOnReady) {
            try { previousOnReady(); } catch (e) { console.error(e); }
        }
        while (callbacks.length > 0) {
            const cb = callbacks.shift();
            if (cb) cb();
        }
    };
};

const extractYouTubeVideoId = (url: string): string | null => {
    try {
        const watchMatch = url.match(/v=([A-Za-z0-9_-]{11})/);
        if (watchMatch) return watchMatch[1];

        const shortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
        if (shortMatch) return shortMatch[1];

        const embedMatch = url.match(/embed\/([A-Za-z0-9_-]{11})/);
        if (embedMatch) return embedMatch[1];

        return null;
    } catch {
        return null;
    }
};

const RestrictedYouTubePlayer = forwardRef<any, RestrictedYouTubePlayerProps>(({
    src,
    onEnded,
    onTimeUpdate,
    onLoadedMetadata,
    onPlay,
    onPause,
    initialTime = 0,
    allowSeeking = false,
    autoPlay = false
}, ref) => {
    const playerRef = useRef<any>(null);
    const progressRef = useRef<HTMLDivElement>(null);
    const currentTimeRef = useRef(initialTime);
    const lastValidTimeRef = useRef(initialTime);
    const durationRef = useRef(0);

    const prevSrcRef = useRef(src);
    const initialTimeRef = useRef(initialTime);

    if (prevSrcRef.current !== src) {
        prevSrcRef.current = src;
        initialTimeRef.current = initialTime;
    }

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(initialTime);
    const [videoDuration, setVideoDuration] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [isBuffering, setIsBuffering] = useState(true);

    const containerId = useMemo(() => `yt-player-${Math.random().toString(36).substring(2, 11)}`, []);

    // Sync initialTime on source/resume change
    useEffect(() => {
        lastValidTimeRef.current = initialTime;
        currentTimeRef.current = initialTime;
        setCurrentTime(initialTime);
    }, [src]);

    // Handle exposed API to mimic standard HTML5 Video Element
    useImperativeHandle(ref, () => ({
        get currentTime() {
            return currentTimeRef.current;
        },
        set currentTime(time: number) {
            if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
                playerRef.current.seekTo(time, true);
                currentTimeRef.current = time;
                setCurrentTime(time);
                lastValidTimeRef.current = time;
            }
        },
        get duration() {
            return durationRef.current;
        },
        play() {
            if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
                playerRef.current.playVideo();
            }
        },
        pause() {
            if (playerRef.current && typeof playerRef.current.pauseVideo === 'function') {
                playerRef.current.pauseVideo();
            }
        }
    }));

    const handlePlayPause = () => {
        if (!playerRef.current || typeof playerRef.current.playVideo !== 'function') return;

        if (isPlaying) {
            playerRef.current.pauseVideo();
        } else {
            playerRef.current.playVideo();
        }
    };

    const handleOverlayClick = () => {
        handlePlayPause();
    };

    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!allowSeeking || !playerRef.current || !progressRef.current || videoDuration === 0) return;
        if (typeof playerRef.current.seekTo !== 'function') return;

        const rect = progressRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const newTime = (clickX / width) * videoDuration;

        playerRef.current.seekTo(newTime, true);
        setCurrentTime(newTime);
        currentTimeRef.current = newTime;
        lastValidTimeRef.current = newTime;
    };

    const formatTime = (seconds: number) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const progressPercent = videoDuration > 0 ? (currentTime / videoDuration) * 100 : 0;

    const handlePlayerReady = useCallback((event: any) => {
        const currentInitTime = initialTimeRef.current;
        console.log('[YouTubePlayer] Ready API event', { initialTime: currentInitTime });
        const player = event.target;
        const duration = player.getDuration() || 0;
        
        durationRef.current = duration;
        setVideoDuration(duration);
        onLoadedMetadata?.({ duration });
        setIsBuffering(false);

        if (currentInitTime > 0 && currentInitTime < duration) {
            player.seekTo(currentInitTime, true);
            lastValidTimeRef.current = currentInitTime;
            currentTimeRef.current = currentInitTime;
            setCurrentTime(currentInitTime);
        }

        if (autoPlay) {
            player.playVideo();
        }
    }, [autoPlay, onLoadedMetadata]);

    const handlePlayerStateChange = useCallback((event: any) => {
        if (!playerRef.current) return;
        const state = event.data;
        console.log('[YouTubePlayer] State Change API:', state);

        if (state === window.YT.PlayerState.PLAYING) {
            setIsPlaying(true);
            setIsBuffering(false);
            onPlay?.();
        } else if (state === window.YT.PlayerState.PAUSED) {
            setIsPlaying(false);
            onPause?.();
        } else if (state === window.YT.PlayerState.BUFFERING) {
            setIsBuffering(true);
        } else if (state === window.YT.PlayerState.ENDED) {
            setIsPlaying(false);
            setIsBuffering(false);
            onEnded?.();
        }
    }, [onPlay, onPause, onEnded]);

    // Handle SDK initialization and destruction
    useEffect(() => {
        let isMounted = true;
        let initTimeout: ReturnType<typeof setTimeout>;

        setIsBuffering(true);
        setError(null);

        const videoId = extractYouTubeVideoId(src);
        if (!videoId) {
            setError('Invalid YouTube video URL');
            setIsBuffering(false);
            return;
        }

        loadYouTubeSDK(() => {
            if (!isMounted) return;

            // Brief delay to ensure DOM ID mounts successfully
            setTimeout(() => {
                if (!isMounted) return;
                try {
                    playerRef.current = new window.YT.Player(containerId, {
                        videoId: videoId,
                        playerVars: {
                            autoplay: autoPlay ? 1 : 0,
                            controls: 0,
                            disablekb: 1,
                            fs: 0,
                            modestbranding: 1,
                            rel: 0,
                            showinfo: 0,
                            iv_load_policy: 3,
                            playsinline: 1
                        },
                        events: {
                            onReady: handlePlayerReady,
                            onStateChange: handlePlayerStateChange,
                            onError: (e: any) => {
                                console.error('[YouTubePlayer] SDK runtime error:', e.data);
                                setError('Failed to load YouTube video. Please try again.');
                                setIsBuffering(false);
                            }
                        }
                    });
                } catch (e) {
                    console.error('[YouTubePlayer] SDK setup exception:', e);
                    setError('Failed to initialize YouTube player.');
                    setIsBuffering(false);
                }
            }, 100);
        });

        // SDK script load timeout
        initTimeout = setTimeout(() => {
            if (isMounted && !playerRef.current) {
                console.error('[YouTubePlayer] API script load timed out.');
                setError('Failed to connect to YouTube. Please check your network.');
                setIsBuffering(false);
            }
        }, 12000);

        return () => {
            isMounted = false;
            clearTimeout(initTimeout);
            if (playerRef.current && typeof playerRef.current.destroy === 'function') {
                try {
                    playerRef.current.destroy();
                } catch (e) {
                    console.error('[YouTubePlayer] Error destroying instance:', e);
                }
            }
        };
    }, [src, containerId, autoPlay, handlePlayerReady, handlePlayerStateChange]);

    // Anti-seeking active time monitor poller
    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;

        if (isPlaying && playerRef.current) {
            interval = setInterval(() => {
                if (!playerRef.current || typeof playerRef.current.getCurrentTime !== 'function') return;

                const ytTime = playerRef.current.getCurrentTime();
                const ytDuration = playerRef.current.getDuration() || 0;

                if (ytDuration > 0 && ytDuration !== durationRef.current) {
                    durationRef.current = ytDuration;
                    setVideoDuration(ytDuration);
                }

                // Check seek policy compliance
                if (!allowSeeking && Math.abs(ytTime - lastValidTimeRef.current) > 1.5) {
                    console.warn(`[YouTubePlayer] Seek violation: jumped to ${ytTime}s. Reverting to ${lastValidTimeRef.current}s.`);
                    playerRef.current.seekTo(lastValidTimeRef.current, true);
                    setCurrentTime(lastValidTimeRef.current);
                    currentTimeRef.current = lastValidTimeRef.current;
                } else {
                    setCurrentTime(ytTime);
                    currentTimeRef.current = ytTime;

                    if (ytTime > lastValidTimeRef.current) {
                        lastValidTimeRef.current = ytTime;
                    }

                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('fastrack:activity'));
                    }

                    onTimeUpdate?.({
                        currentTime: ytTime,
                        duration: ytDuration,
                        percentWatched: ytDuration > 0 ? (ytTime / ytDuration) * 100 : 0
                    });
                }
            }, 250);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isPlaying, allowSeeking, onTimeUpdate]);

    // Intercept keyboard hotkeys to block skipping bypasses
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (allowSeeking) {
                if (e.key === 'ArrowRight' && playerRef.current && typeof playerRef.current.seekTo === 'function') {
                    const newTime = Math.min(videoDuration, currentTimeRef.current + 5);
                    playerRef.current.seekTo(newTime, true);
                    return;
                }
                if (e.key === 'ArrowLeft' && playerRef.current && typeof playerRef.current.seekTo === 'function') {
                    const newTime = Math.max(0, currentTimeRef.current - 5);
                    playerRef.current.seekTo(newTime, true);
                    return;
                }
                return;
            }

            const seekKeys = ['ArrowLeft', 'ArrowRight', 'j', 'f', 'l'];
            if (seekKeys.includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [allowSeeking, videoDuration]);

    return (
        <div className={styles.container}>
            {error && (
                <div className={styles.error}>
                    <div className={styles.errorContent}>
                        <div className={styles.errorIcon}>⚠️</div>
                        <div className={styles.errorMessage}>{error}</div>
                    </div>
                </div>
            )}

            <div className={styles.playerContent} style={{ display: error ? 'none' : 'block' }}>
                <div className={styles.videoWrapper}>
                    {isBuffering && (
                        <div className={styles.bufferingOverlay}>
                            <div className={styles.spinner} />
                            <div className={styles.bufferingText}>Buffering...</div>
                        </div>
                    )}
                    <div className={styles.iframeContainer}>
                        <div id={containerId} className={styles.youtubeIframe} />
                        <div className={styles.clickOverlay} onClick={handleOverlayClick} />
                    </div>
                </div>

                <div className={styles.customControls}>
                    <div
                        ref={progressRef}
                        className={`${styles.progressContainer} ${allowSeeking ? styles.seekableContainer : ''}`}
                        onClick={handleProgressBarClick}
                    >
                        <div
                            className={`${styles.progressBar} ${allowSeeking ? styles.seekable : ''}`}
                            style={{ width: `${progressPercent}%` }}
                        />
                    </div>

                    <div className={styles.controlsBottom}>
                        <button
                            className={styles.playButton}
                            onClick={handlePlayPause}
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                            disabled={isBuffering && videoDuration === 0}
                        >
                            {isPlaying ? '⏸' : '▶'}
                        </button>

                        <div className={styles.timeDisplay}>
                            <span>{formatTime(currentTime)}</span>
                            <span> / </span>
                            <span>{formatTime(videoDuration)}</span>
                        </div>

                        <div className={styles.seekWarning}>
                            {isBuffering ? '⏳ Buffering...' : allowSeeking ? '✅ Seeking enabled' : '⚠️ Seeking disabled (compliance requirement)'}
                        </div>
                    </div>
                </div>

                <div className={styles.note}>
                    {allowSeeking
                        ? "Note: You have completed this video. Seeking is now available."
                        : "Note: You must watch the entire video to proceed. Seeking is disabled to ensure content compliance."}
                </div>
            </div>
        </div>
    );
});

RestrictedYouTubePlayer.displayName = 'RestrictedYouTubePlayer';

export default RestrictedYouTubePlayer;
